import React, { FC, useMemo, useState } from 'react';

import { addMinutes } from 'date-fns';
import { FormikProps } from 'formik';

// components
import BasicButton from '../../components/buttons/basicButton';
import AampeDatePicker from '../../components/inputs/datepicker/AampeDatePicker';
import AampeTimePicker from '../../components/inputs/datepicker/AampeTimePicker';
//components
import BasicModal from '../../components/modals/basicModal';
import styled from 'styled-components';

// theme
import aampeTheme from '../../constants/aampeTheme';
// constants
import { CONTROLLED_FORMULA_BUFFER } from '../../constants/formula';
import ICONS from '../../constants/icons';

// helpers
import { formatISODate, getUTCDate } from '../../helpers/dateTimeHelpers';

import useRoleFlags from 'hooks/auth/useRoleFlags';

// models
import { FormulaViewSetupFormValue } from '../../models/formula.model';

interface FormulaStartTimeProps {
    handleClose: () => void;
    formikProps: FormikProps<FormulaViewSetupFormValue>;
}
const FormulaStartTimeEdit: FC<React.PropsWithChildren<FormulaStartTimeProps>> =
    ({ handleClose, formikProps }) => {
        const { hasWriteAccess } = useRoleFlags();

        const isDisabled = useMemo<boolean>(() => {
            return !hasWriteAccess;
        }, [hasWriteAccess]);

        const initialUpdatedTime = useMemo<Date>(
            () => addMinutes(new Date(), CONTROLLED_FORMULA_BUFFER),
            [],
        );

        const [startDate, setStartDate] = useState<Date>(initialUpdatedTime);
        const minDate = useMemo<Date>(() => getUTCDate(), []);
        return (
            <BasicModal
                headerStyle={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'centers',
                    padding: aampeTheme.spacing(2, 3),
                }}
                header="Update message send time"
                width="650px"
                handleClose={handleClose}
            >
                <Container>
                    <DisclaimerWithIconContainer>
                        <IconContainer>
                            <ICONS.info size={18} />
                        </IconContainer>
                        <DisclaimerContainer>
                            <SubLabel>
                                There should be a minimum time gap of{' '}
                                {CONTROLLED_FORMULA_BUFFER} minutes between the
                                scheduled message sending time and the current
                                time.
                            </SubLabel>
                        </DisclaimerContainer>
                    </DisclaimerWithIconContainer>
                    <DateTimeContainer>
                        <Label> Select message send time:</Label>
                        <InputSection>
                            <AampeDatePickerWrapper>
                                <Header>Date</Header>
                                <div>
                                    <AampeDatePicker
                                        selectedDate={startDate}
                                        setSelectedDate={(date: Date) =>
                                            setStartDate(date)
                                        }
                                        minDate={minDate}
                                        maxDate={null}
                                        isClearable={false}
                                        isDisabled={isDisabled}
                                    />
                                </div>
                            </AampeDatePickerWrapper>
                            <AampeDatePickerWrapper>
                                <Header>Time</Header>
                                <AampeTimePicker
                                    selectedDate={startDate}
                                    setSelectedDate={(date: Date) =>
                                        setStartDate(date)
                                    }
                                    isDisabled={isDisabled}
                                />
                            </AampeDatePickerWrapper>
                        </InputSection>
                    </DateTimeContainer>
                    <Actions>
                        <BasicButton
                            name="Cancel"
                            isPrimary={false}
                            isCompact
                            onClick={() => setStartDate(initialUpdatedTime)}
                            type="reset"
                        />
                        <BasicButton
                            onClick={() => {
                                formikProps.setFieldValue(
                                    'send_start',
                                    formatISODate(startDate).split('+')[0],
                                );
                                formikProps.handleSubmit();
                                handleClose();
                            }}
                            isDisabled={isDisabled}
                            name="Save"
                            isCompact
                            type="submit"
                        />
                    </Actions>
                </Container>
            </BasicModal>
        );
    };

const Header = styled.span({
    color: aampeTheme.colors.neutral09,
    marginTop: aampeTheme.spacing(0.25),
});

const Container = styled.div(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing(2),
}));
const InputSection = styled.div({
    display: 'flex',
    border: `1px solid ${aampeTheme.colors.bordersWG01}`,
    borderRadius: aampeTheme.borderRadius.default,
    backgroundColor: aampeTheme.colors.white,
    color: aampeTheme.colors.gray800,
    fontSize: '14px',
    fontWeight: 400,
    padding: aampeTheme.spacing(0.5, 1),
    width: 'fit-content',
    alignItems: 'center',
    marginTop: aampeTheme.spacing(1),
});
const AampeDatePickerWrapper = styled.div({
    display: 'flex',
    alignItems: 'center',
});

const Label = styled.span({
    color: aampeTheme.colors.gray800,
    fontSize: `14px`,
    lineHeight: `20px`,
    fontWeight: `bold`,
});
const DateTimeContainer = styled.div({
    marginTop: aampeTheme.spacing(2),
});
const DisclaimerWithIconContainer = styled.div({
    display: `flex`,
    alignItems: 'flex-start',
    backgroundColor: aampeTheme.colors.backgroundWG01,
    padding: aampeTheme.spacing(2, 2, 2, 0),
    borderRadius: aampeTheme.borderRadius.default,
});

const IconContainer = styled.span({
    padding: aampeTheme.spacing(0, 1.5),
    color: aampeTheme.colors.textAccessibleWG01,
});

const DisclaimerContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing(1),
});
const SubLabel = styled.div({
    fontSize: `14px`,
    lineHeight: `20px`,
    color: aampeTheme.colors.gray800,
    fontWeight: 400,
});
const Actions = styled.div(() => ({
    display: 'flex',
    gap: aampeTheme.spacing(2),
    alignSelf: 'self-end',
}));

export default FormulaStartTimeEdit;
